/** Catálogo de painéis do BI — acesso por utilizador (ver / escrever).
 * Só o menu Business visível hoje (fora Ctrl+H / preferências de UI).
 */

export type PanelAccess = { ver: boolean; escrever: boolean }

export type PanelDef = {
  id: string
  label: string
  path: string
  exact?: boolean
  group: string
  /** Painel tem acções de escrita no BI. */
  podeEscrever: boolean
  /** Só admin (ex.: Usuários). */
  adminOnly?: boolean
  /** Ligado por defeito para Operador / Leitura. */
  defaultOperador: boolean
}

/** Ordem e grupos = menu lateral visível (sem itens só Ctrl+H). */
export const PANEL_CATALOG: PanelDef[] = [
  { id: 'vendas_diarias', label: 'Vendas Diárias', path: '/', exact: true, group: 'Operação', podeEscrever: false, defaultOperador: true },
  { id: 'vendas_anuncio', label: 'Vendas por Anúncio', path: '/produtos', group: 'Operação', podeEscrever: false, defaultOperador: true },
  { id: 'margem_anuncios', label: 'Margem por Anúncio', path: '/margem-anuncios', group: 'Operação', podeEscrever: false, defaultOperador: true },
  { id: 'natal_2026', label: 'Natal 2026', path: '/natal-2026', group: 'Operação', podeEscrever: true, defaultOperador: true },
  { id: 'estoque_full', label: 'Estoque Full', path: '/estoque-full', group: 'Operação', podeEscrever: false, defaultOperador: true },
  { id: 'expedicao', label: 'Expedição', path: '/expedicao', group: 'Operação', podeEscrever: true, defaultOperador: true },
  { id: 'reclamacoes', label: 'Reclamações', path: '/reclamacoes', group: 'Marketplace', podeEscrever: false, defaultOperador: true },
  { id: 'recompra', label: 'Recompra', path: '/recompra', group: 'Marketplace', podeEscrever: true, defaultOperador: true },
  { id: 'promocoes', label: 'Promoções', path: '/promocoes', group: 'Marketplace', podeEscrever: true, defaultOperador: true },
  { id: 'ads', label: 'Ads', path: '/ads', group: 'Marketplace', podeEscrever: true, defaultOperador: true },
  { id: 'score', label: 'Score', path: '/score-anuncios', group: 'Marketplace', podeEscrever: false, defaultOperador: true },
  { id: 'saude_anuncios', label: 'Saúde dos Anúncios', path: '/validador-anuncios', group: 'Marketplace', podeEscrever: false, defaultOperador: true },
  { id: 'buybox', label: 'Buy Box', path: '/buybox', group: 'Marketplace', podeEscrever: false, defaultOperador: true },
  { id: 'marcas', label: 'Consulta Marcas INPI', path: '/marcas', group: 'Marketplace', podeEscrever: false, defaultOperador: true },
  { id: 'fabrica_anuncios', label: 'Fábrica de Anúncios', path: '/fabrica-anuncios', group: 'Fábricas', podeEscrever: true, defaultOperador: true },
  { id: 'fabrica_videos', label: 'Fábrica de Vídeos', path: '/fabrica-videos', group: 'Fábricas', podeEscrever: true, defaultOperador: true },
  { id: 'agentes', label: 'Agentes', path: '/agentes', group: 'Fábricas', podeEscrever: false, defaultOperador: true },
  { id: 'usuarios', label: 'Usuários', path: '/usuarios', group: 'Sistema', podeEscrever: true, adminOnly: true, defaultOperador: false },
]

export type PanelId = (typeof PANEL_CATALOG)[number]['id']

export type PaineisMap = Record<string, PanelAccess>

export function defaultPaineisForTipo(tipo: 'admin' | 'usuario' | 'leitura'): PaineisMap {
  const out: PaineisMap = {}
  for (const p of PANEL_CATALOG) {
    if (tipo === 'admin') {
      out[p.id] = { ver: true, escrever: true }
      continue
    }
    if (p.adminOnly) {
      out[p.id] = { ver: false, escrever: false }
      continue
    }
    const ver = p.defaultOperador
    const escrever = tipo === 'leitura' ? false : ver && p.podeEscrever
    out[p.id] = { ver, escrever }
  }
  return out
}

export function normalizePaineis(
  raw: unknown,
  tipo: 'admin' | 'usuario' | 'leitura',
): PaineisMap {
  if (tipo === 'admin') return defaultPaineisForTipo('admin')
  const base = defaultPaineisForTipo(tipo)
  if (!raw || typeof raw !== 'object') return base
  const src = raw as Record<string, unknown>
  for (const p of PANEL_CATALOG) {
    if (p.adminOnly) {
      base[p.id] = { ver: false, escrever: false }
      continue
    }
    const cell = src[p.id]
    if (!cell || typeof cell !== 'object') continue
    const c = cell as { ver?: unknown; escrever?: unknown }
    const ver = Boolean(c.ver)
    let escrever = Boolean(c.escrever) && p.podeEscrever
    if (tipo === 'leitura') escrever = false
    if (!ver) escrever = false
    base[p.id] = { ver, escrever }
  }
  return base
}

export function panelGroups(): { group: string; panels: PanelDef[] }[] {
  const order: string[] = []
  const map = new Map<string, PanelDef[]>()
  for (const p of PANEL_CATALOG) {
    if (!map.has(p.group)) {
      order.push(p.group)
      map.set(p.group, [])
    }
    map.get(p.group)!.push(p)
  }
  return order.map((group) => ({ group, panels: map.get(group)! }))
}

export function panelIdForPath(pathname: string): PanelId | null {
  const path = pathname.split('?')[0] || '/'
  if (path === '/insights' || path.startsWith('/insights/')) return null
  let best: PanelDef | null = null
  for (const p of PANEL_CATALOG) {
    const ok = p.exact ? path === p.path : path === p.path || path.startsWith(`${p.path}/`)
    if (!ok) continue
    if (!best || p.path.length > best.path.length) best = p
  }
  return best ? (best.id as PanelId) : null
}

/** Liga DomainNav `to` → id do painel. */
export function panelIdForNavTo(to: string): PanelId | null {
  const p = PANEL_CATALOG.find((x) => x.path === to || (x.exact && to === '/'))
  return p ? (p.id as PanelId) : null
}
